/* =================================================================
   Fygr Tour Engine — portable, vanilla JS.
   Ported from proto-onboarding-module/project/tour-engine.js.

   Public API on window.FygrTour:
     FygrTour.register(id, config)
     FygrTour.start(id)
     FygrTour.next()
     FygrTour.exit({ silent })
     FygrTour.isActive()
     FygrTour.autoStart()  // reads ?tour=xxx from URL

   In Vue templates, attach attributes:
     <button data-tour-step="comptes-pill">Comptes</button>
   ================================================================= */

(function () {
  'use strict';

  const tours = new Map();
  let active = null;

  const Api = {
    register(id, config) {
      tours.set(id, Object.assign({ id }, config));
    },
    start(id) {
      const cfg = tours.get(id);
      if (!cfg) { console.warn('[FygrTour] Unknown tour:', id); return; }
      if (active) Api.exit();
      active = { config: cfg, stepIndex: 0, nodes: {} };
      // Mitigation A : si mount() ou showStep() throw, on nettoie tout
      // pour ne pas laisser le dashboard gelé avec des listeners orphelins.
      try {
        mount();
        showStep(0);
      } catch (err) {
        console.error('[FygrTour] mount/start failed, auto-exit:', err);
        Api.exit({ silent: true });
      }
    },
    next() {
      if (!active) return;
      const i = active.stepIndex + 1;
      if (i >= active.config.steps.length) { celebrate(); return; }
      showStep(i);
    },
    exit({ silent = false } = {}) {
      if (!active) return;
      cleanupTargetHighlight();
      const onMove = active.onMove;
      Object.values(active.nodes).forEach((n) => n && n.remove && n.remove());
      document.body.classList.remove('tour-active');
      const cfg = active.config;
      active = null;
      if (onMove) {
        window.removeEventListener('resize', onMove);
        window.removeEventListener('scroll', onMove, { capture: true });
      }
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', blockOutsideClick, { capture: true });
      document.removeEventListener('mousedown', blockOutsideClick, { capture: true });
      document.removeEventListener('pointerdown', blockOutsideClick, { capture: true });
      if (!silent && cfg.onExit) cfg.onExit();
    },
    isActive() { return !!active; },
    autoStart() {
      const params = new URLSearchParams(window.location.search);
      const id = params.get('tour');
      if (id && tours.has(id)) {
        setTimeout(() => Api.start(id), 50);
      }
    },
  };

  function mount() {
    document.body.classList.add('tour-active');
    const cfg = active.config;

    const rail = el('aside', 'tour-rail');
    rail.innerHTML = `
      <div class="tour-rail__icon">◈</div>
      <h2 class="tour-rail__title"></h2>
      <p class="tour-rail__step-label"></p>
      <div class="tour-rail__card">
        <div class="tour-rail__card-title"></div>
        <div class="tour-rail__card-body"></div>
      </div>
      <p class="tour-rail__progress-label">Progression</p>
      <div class="tour-rail__progress"></div>
      <div class="tour-rail__spacer"></div>
      <button class="tour-rail__exit" type="button">
        <span>Quitter le tutoriel</span>
        <span class="tour-rail__exit-x">×</span>
      </button>
    `;
    rail.querySelector('.tour-rail__title').textContent = cfg.title;
    rail.querySelector('.tour-rail__exit').addEventListener('click', () => Api.exit());
    // Force le positionnement critique inline : indépendant du SCSS, immune au cache Vite.
    rail.style.position = 'fixed';
    rail.style.top = '0';
    rail.style.left = '0';
    rail.style.bottom = '0';
    rail.style.width = '260px';
    rail.style.zIndex = '9000';
    rail.style.background = '#2D0A3E';
    rail.style.color = '#FFFFFF';
    document.body.appendChild(rail);

    // Overlay = spotlight body-level : transparent + box-shadow géante qui dimme,
    // + border aubergine qui forme l'anneau. Placé hors de tout overflow:hidden.
    const overlay = el('div', 'tour-overlay');
    overlay.style.cssText = [
      'position:fixed',
      'top:0',
      'left:0',
      'width:0',
      'height:0',
      'background:transparent',
      'box-shadow:0 0 0 9999px rgba(0,0,0,0.5)',
      'border:2px solid #2D0A3E',
      'border-radius:0',
      'outline:0 solid rgba(45,10,62,0)',
      'outline-offset:2px',
      'animation:tourHalo 1800ms ease-in-out infinite',
      'z-index:8900',
      'pointer-events:none',
      'box-sizing:border-box',
      'transition:top 200ms ease, left 200ms ease, width 200ms ease, height 200ms ease, border-radius 200ms ease',
    ].join(';') + ';';
    document.body.appendChild(overlay);

    const bubble = el('div', 'tour-bubble');
    bubble.innerHTML = `
      <div class="tour-bubble__caret"></div>
      <div class="tour-bubble__head">
        <span class="tour-bubble__badge"></span>
        <button class="tour-bubble__skip" type="button">Passer le tuto</button>
      </div>
      <div class="tour-bubble__title"></div>
      <div class="tour-bubble__body"></div>
      <div class="tour-bubble__foot">
        <div class="tour-bubble__dots"></div>
      </div>
    `;
    // Force position + z-index inline pour garantir le stacking au-dessus de l'overlay.
    bubble.style.position = 'fixed';
    bubble.style.zIndex = '9100';
    bubble.querySelector('.tour-bubble__skip').addEventListener('click', () => Api.exit());
    document.body.appendChild(bubble);

    const fab = el('button', 'tour-skip-fab');
    fab.type = 'button';
    fab.style.position = 'fixed';
    fab.style.bottom = '24px';
    fab.style.right = '24px';
    fab.style.zIndex = '9100';
    fab.innerHTML = `<span class="material-symbols-outlined">close</span><span>Passer le tutoriel</span>`;
    fab.addEventListener('click', () => Api.exit());
    document.body.appendChild(fab);

    active.nodes = { rail, overlay, bubble, fab };

    const onMove = () => { positionBubble(); updateOverlayClip(); };
    window.addEventListener('resize', onMove, { passive: true });
    window.addEventListener('scroll', onMove, { passive: true, capture: true });
    active.onMove = onMove;
    document.addEventListener('keydown', onKey);
    // Bloque tous les clics hors de la cible et de l'UI tour, pour que
    // l'utilisateur ne puisse interagir qu'avec l'élément visé.
    document.addEventListener('click', blockOutsideClick, { capture: true });
    document.addEventListener('mousedown', blockOutsideClick, { capture: true });
    document.addEventListener('pointerdown', blockOutsideClick, { capture: true });
  }

  function onKey(e) { if (e.key === 'Escape' && active) Api.exit(); }

  function blockOutsideClick(e) {
    if (!active) return;
    // Mitigation B : si la cible a été retirée du DOM (Vue re-render, navigation
    // côté composant, etc.), on quitte le tour proprement plutôt que de geler
    // la page sans cible cliquable possible.
    if (active.currentTarget && !document.body.contains(active.currentTarget)) {
      Api.exit({ silent: true });
      return;
    }
    const t = e.target;
    if (t.closest('.tour-rail, .tour-bubble, .tour-skip-fab')) return;
    if (active.currentTarget && (active.currentTarget === t || active.currentTarget.contains(t))) return;
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
  }

  function showStep(idx) {
    if (!active) return;
    active.stepIndex = idx;
    const step = active.config.steps[idx];
    if (!step) return;

    cleanupTargetHighlight();

    waitForTarget(step.target, 1500).then((target) => {
      if (!active || active.stepIndex !== idx) return;
      if (!target) {
        console.warn('[FygrTour] Target not found:', step.target);
        return;
      }
      active.currentTarget = target;
      target.classList.add('tour-target--active');
      // Sauvegarde + force le stacking au-dessus de l'overlay
      active.targetPrevZ = target.style.zIndex || '';
      active.targetPrevPos = target.style.position || '';
      if (getComputedStyle(target).position === 'static') target.style.position = 'relative';
      target.style.zIndex = '9050';

      target.addEventListener('click', onTargetClick, { capture: true, once: false });
      active.currentClickHandler = onTargetClick;

      updateRail();
      updateBubble();
      try { target.scrollIntoView({ block: 'nearest', behavior: 'instant' }); } catch { /* noop */ }
      // Mesure différée : on attend que les animations CSS (expandY 0.25s sur les
      // dropdowns FYGR, etc.) soient terminées avant de calculer le spotlight.
      const measure = () => {
        if (!active || active.currentTarget !== target) return;
        positionBubble();
        updateOverlayClip();
      };
      requestAnimationFrame(measure);
      setTimeout(measure, 320);
    });
  }

  function updateOverlayClip() {
    if (!active || !active.currentTarget || !active.nodes) return;
    const r = active.currentTarget.getBoundingClientRect();
    const cs = getComputedStyle(active.currentTarget);
    const baseRadius = cs.borderRadius && cs.borderRadius !== '0px' ? cs.borderRadius : '6px';
    // Petit gap entre la cible et l'anneau (similaire à un focus-ring offset).
    const pad = 3;
    const o = active.nodes.overlay;
    o.style.top = `${r.top - pad}px`;
    o.style.left = `${r.left - pad}px`;
    o.style.width = `${r.width + pad * 2}px`;
    o.style.height = `${r.height + pad * 2}px`;
    o.style.borderRadius = baseRadius;
  }

  function resetOverlayClip() {
    if (!active || !active.nodes || !active.nodes.overlay) return;
    const o = active.nodes.overlay;
    o.style.top = '0';
    o.style.left = '0';
    o.style.width = '0';
    o.style.height = '0';
    o.style.borderRadius = '0';
  }

  function onTargetClick() {
    if (!active) return;
    const step = active.config.steps[active.stepIndex];
    if (step.waitFor) {
      waitForTarget(step.waitFor, 2000).then((next) => {
        if (next && active && active.stepIndex === active.config.steps.indexOf(step)) {
          Api.next();
        }
      });
    } else {
      setTimeout(() => Api.next(), 60);
    }
  }

  function cleanupTargetHighlight() {
    if (!active) return;
    if (active.currentTarget) {
      active.currentTarget.classList.remove('tour-target--active', 'is-bouncing');
      active.currentTarget.style.zIndex = active.targetPrevZ || '';
      active.currentTarget.style.position = active.targetPrevPos || '';
      if (active.currentClickHandler) {
        active.currentTarget.removeEventListener('click', active.currentClickHandler, { capture: true });
      }
      active.currentTarget = null;
      active.currentClickHandler = null;
    }
    resetOverlayClip();
  }

  function updateRail() {
    const cfg = active.config;
    const idx = active.stepIndex;
    const step = cfg.steps[idx];
    const total = cfg.steps.length;
    const rail = active.nodes.rail;

    rail.querySelector('.tour-rail__step-label').textContent = `Étape ${idx + 1} sur ${total}`;
    rail.querySelector('.tour-rail__card-title').textContent = step.title;
    rail.querySelector('.tour-rail__card-body').textContent = step.body;

    const progress = rail.querySelector('.tour-rail__progress');
    progress.innerHTML = '';
    for (let i = 0; i < total; i++) {
      const seg = el('div', 'tour-rail__progress-seg');
      if (i < idx) seg.classList.add('is-done');
      else if (i === idx) seg.classList.add('is-current');
      progress.appendChild(seg);
    }
  }

  function updateBubble() {
    const cfg = active.config;
    const idx = active.stepIndex;
    const step = cfg.steps[idx];
    const total = cfg.steps.length;
    const b = active.nodes.bubble;

    b.querySelector('.tour-bubble__badge').textContent = step.badge || `Étape ${idx + 1} / ${total}`;
    b.querySelector('.tour-bubble__title').textContent = step.title;
    b.querySelector('.tour-bubble__body').textContent = step.body;

    const dots = b.querySelector('.tour-bubble__dots');
    dots.innerHTML = '';
    for (let i = 0; i < total; i++) {
      const d = el('span', 'tour-bubble__dot');
      if (i < idx) d.classList.add('is-done');
      else if (i === idx) d.classList.add('is-current');
      dots.appendChild(d);
    }

    b.setAttribute('data-placement', step.placement || 'bottom-start');
    requestAnimationFrame(() => b.classList.add('is-shown'));
  }

  function positionBubble() {
    if (!active || !active.currentTarget) return;
    const b = active.nodes.bubble;
    const step = active.config.steps[active.stepIndex];
    const placement = step.placement || 'bottom-start';

    const t = active.currentTarget.getBoundingClientRect();
    const bw = b.offsetWidth || 280;
    const bh = b.offsetHeight || 160;
    const gap = 8;
    const pad = 16;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top;
    let left;
    if (placement.startsWith('bottom')) {
      top = t.bottom + gap;
      if (placement === 'bottom-end') left = t.right - bw;
      else if (placement === 'bottom') left = t.left + (t.width - bw) / 2;
      else left = t.left;
    } else if (placement.startsWith('top')) {
      top = t.top - bh - gap;
      if (placement === 'top-end') left = t.right - bw;
      else if (placement === 'top') left = t.left + (t.width - bw) / 2;
      else left = t.left;
    } else if (placement.startsWith('right')) {
      left = t.right + gap;
      top = (placement === 'right-start') ? t.top : (t.top + (t.height - bh) / 2);
    } else if (placement.startsWith('left')) {
      left = t.left - bw - gap;
      top = (placement === 'left-start') ? t.top : (t.top + (t.height - bh) / 2);
    }

    left = Math.max(pad, Math.min(left, vw - bw - pad));
    top = Math.max(pad, Math.min(top, vh - bh - pad));

    b.style.top = `${top}px`;
    b.style.left = `${left}px`;

    // Aligne le caret de la bulle sur le centre vertical/horizontal de la cible.
    const caret = b.querySelector('.tour-bubble__caret');
    if (caret) {
      if (placement.startsWith('right') || placement.startsWith('left')) {
        const targetCenterY = t.top + t.height / 2;
        const caretY = targetCenterY - top - 6; // 6 = demi-hauteur du caret (12px)
        caret.style.top = `${Math.max(8, Math.min(caretY, bh - 20))}px`;
      } else {
        caret.style.top = '';
      }
    }
  }

  function celebrate() {
    if (!active) return;
    const cfg = active.config;
    const cel = cfg.celebration || {};

    active.nodes.bubble.style.opacity = '0';
    setTimeout(() => active.nodes.bubble && active.nodes.bubble.remove(), 220);
    active.nodes.overlay.style.opacity = '0';
    setTimeout(() => active.nodes.overlay && active.nodes.overlay.remove(), 220);

    const toast = el('div', 'tour-toast');
    toast.innerHTML = `
      <div class="tour-toast__icon"><span class="material-symbols-outlined">check</span></div>
      <div class="tour-toast__content">
        <div class="tour-toast__title">${cel.title || 'Module terminé'}</div>
        <div class="tour-toast__body">${cel.body || ''}</div>
      </div>
      <button class="tour-toast__close" type="button" aria-label="Fermer">×</button>
    `;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('is-shown'));

    const close = () => {
      toast.classList.remove('is-shown');
      setTimeout(() => toast.remove(), 240);
      try {
        const completed = JSON.parse(localStorage.getItem('fygr-tours-completed') || '[]');
        if (!completed.includes(cfg.id)) {
          completed.push(cfg.id);
          localStorage.setItem('fygr-tours-completed', JSON.stringify(completed));
        }
      } catch { /* ignore */ }
      Api.exit({ silent: true });
      if (cel.returnTo) {
        setTimeout(() => { window.location.href = cel.returnTo; }, 260);
      } else if (cel.followup) {
        // Une fois le bandeau aubergine retiré et la sidenav revenue,
        // on affiche une bulle de follow-up pour inviter l'utilisateur à
        // explorer ou retourner dans Modules.
        setTimeout(() => showFollowup(cel.followup), 400);
      }
    };
    toast.querySelector('.tour-toast__close').addEventListener('click', close);
    setTimeout(close, 3500);

    cleanupTargetHighlight();
  }

  function showFollowup(fu) {
    const bubble = el('div', 'tour-followup');
    bubble.innerHTML = `
      <div class="tour-followup__icon"><span class="material-symbols-outlined">rocket_launch</span></div>
      <div class="tour-followup__content">
        <div class="tour-followup__title">${fu.title || ''}</div>
        <div class="tour-followup__body">${fu.body || ''}</div>
      </div>
      <button class="tour-followup__close" type="button" aria-label="Fermer">×</button>
      <span class="tour-followup__dot" aria-hidden="true"></span>
    `;
    document.body.appendChild(bubble);
    requestAnimationFrame(() => bubble.classList.add('is-shown'));
    const dismiss = () => {
      bubble.classList.remove('is-shown');
      setTimeout(() => bubble.remove(), 260);
    };
    bubble.querySelector('.tour-followup__close').addEventListener('click', dismiss);
    setTimeout(dismiss, 7000);
  }

  function el(tag, className) {
    const e = document.createElement(tag);
    if (className) e.className = className;
    return e;
  }

  function waitForTarget(selector, timeoutMs = 1500) {
    return new Promise((resolve) => {
      const existing = document.querySelector(selector);
      if (existing) return resolve(existing);
      const start = Date.now();
      const iv = setInterval(() => {
        const found = document.querySelector(selector);
        if (found) { clearInterval(iv); resolve(found); }
        else if (Date.now() - start > timeoutMs) { clearInterval(iv); resolve(null); }
      }, 50);
    });
  }

  window.FygrTour = Api;
})();
